/** biome-ignore-all lint/style/useNamingConvention: Optimizely event properties use snake_case */
'use client';

import { ChevronUp, Loader2, Pause, Play, Volume2, VolumeX, X } from 'lucide-react';

import { getUserContext } from '@/app_lib/optimizely/utils';
import type { PageAdConfig } from '@/app_lib/types/Ad';
import { useOptimizely } from '@/main/lib/hooks/useOptimizely';
import type { VariantProps } from '@/main/lib/providers/OptimizelyProvider';

const SEEK_STEP_SECONDS = 5;
const MAX_VIDEOS_FOR_TIME_SPENT = 7;
const PROGRESS_PERCENT_DIVISOR = 100;
const CONTROLS_HIDE_DELAY_MS = 4000;
const OVERSCROLL_DAMPING = 0.5;
const SCROLL_ANIMATION_DURATION_MS = 280;

function easeOutCubic(t: number): number {
  return 1 - (1 - t) ** 3;
}
const PRELOAD_INITIAL_COUNT = 3;
const PRELOAD_NEXT_COUNT = 3;
/** HTMLMediaElement.HAVE_FUTURE_DATA - enough data to play without buffering */
const VIDEO_READY_STATE_HAVE_FUTURE_DATA = 3;

import { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useInView } from 'react-intersection-observer';
import { cn } from '@/app_lib/utils/utils';

interface VideoItem {
  id: string;
  videoUrl: string;
  thumbnailUrl?: string;
  title?: string;
  description?: string;
}

interface VerticalVideoPlayerTriggerProps {
  /** Call to open the fullscreen player */
  onActivate: () => void;
  /** Attach to the element used for scroll-in-view detection and expand animation rect (use a div wrapper if needed) */
  setTriggerRef: (el: HTMLDivElement | null) => void;
}

interface VerticalVideoPlayerProps {
  videos: VideoItem[];
  initialVideoIndex?: number;
  onClose?: () => void;
  onVideoChange?: (videoIndex: number) => void;
  /**
   * Custom trigger element. Receives onActivate (click handler) and setTriggerRef (for scroll detection + expand rect).
   * Required - no default trigger is provided.
   */
  renderTrigger: (props: VerticalVideoPlayerTriggerProps) => React.ReactNode;
  /** When true, scroll-in-view only fires once. Use when trigger has no click (e.g. RelatedVideo). */
  scrollTriggerOnce?: boolean;
}

interface VideoControlsProps {
  isPlaying: boolean;
  isMuted: boolean;
  progress: { currentTime: number; duration: number };
  onPlayPause: () => void;
  onMuteToggle: () => void;
  onSeek: (e: React.MouseEvent<HTMLDivElement>) => void;
  onSeekComplete?: (newTime: number, duration: number) => void;
  videoRef: React.RefObject<(HTMLVideoElement | null)[]>;
  currentIndex: number;
}

/** Video controls overlay - play/pause, mute, scrubber */
function VideoControls({
  isPlaying,
  isMuted,
  progress,
  onPlayPause,
  onMuteToggle,
  onSeek,
  onSeekComplete,
  videoRef,
  currentIndex,
}: VideoControlsProps) {
  const handleKeyDown = (e: React.KeyboardEvent) => {
    const currentVideo = videoRef.current?.[currentIndex];
    if (!currentVideo?.duration) {
      return;
    }
    let step = 0;
    if (e.key === 'ArrowRight') {
      step = SEEK_STEP_SECONDS;
    } else if (e.key === 'ArrowLeft') {
      step = -SEEK_STEP_SECONDS;
    }
    if (step) {
      e.preventDefault();
      const newTime = Math.max(0, Math.min(progress.duration, progress.currentTime + step));
      currentVideo.currentTime = newTime;
      onSeekComplete?.(newTime, progress.duration);
    }
  };

  return (
    <div
      className={cn(
        'absolute inset-x-0 bottom-0 z-[10001]',
        'bg-[linear-gradient(to_top,rgba(0,0,0,0.8)_0%,rgba(0,0,0,0.4)_60%,transparent_100%)]',
        'px-5 pb-6 pt-4',
      )}
    >
      <div
        aria-label='Video progress'
        aria-orientation='horizontal'
        aria-valuemax={progress.duration}
        aria-valuemin={0}
        aria-valuenow={progress.currentTime}
        aria-valuetext={
          progress.duration > 0
            ? `${Math.floor(progress.currentTime)} of ${Math.floor(progress.duration)} seconds`
            : undefined
        }
        className='mb-3 h-1 cursor-pointer overflow-hidden rounded-[2px] bg-white/30 opacity-90'
        onClick={(e) => {
          e.stopPropagation();
          onSeek(e);
        }}
        onKeyDown={handleKeyDown}
        role='slider'
        tabIndex={0}
      >
        <div
          className='h-full rounded-[2px] bg-white transition-[width] duration-100 ease-linear'
          style={{
            width: `${progress.duration ? (progress.currentTime / progress.duration) * PROGRESS_PERCENT_DIVISOR : 0}%`,
          }}
        />
      </div>
      <div className='flex items-center justify-between gap-4'>
        <button
          aria-label={isPlaying ? 'Pause' : 'Play'}
          className='flex min-h-[44px] min-w-[44px] cursor-pointer items-center justify-center border-none bg-transparent p-2 text-white opacity-90'
          onClick={(e) => {
            e.stopPropagation();
            onPlayPause();
          }}
          type='button'
        >
          {isPlaying ? (
            <Pause
              size={28}
              strokeWidth={2}
            />
          ) : (
            <Play
              fill='currentColor'
              size={28}
              strokeWidth={2}
            />
          )}
        </button>
        <button
          aria-label={isMuted ? 'Unmute' : 'Mute'}
          className='flex min-h-[44px] min-w-[44px] cursor-pointer items-center justify-center border-none bg-transparent p-2 text-white opacity-90'
          onClick={(e) => {
            e.stopPropagation();
            onMuteToggle();
          }}
          type='button'
        >
          {isMuted ? (
            <VolumeX
              size={24}
              strokeWidth={2}
            />
          ) : (
            <Volume2
              size={24}
              strokeWidth={2}
            />
          )}
        </button>
      </div>
    </div>
  );
}

interface ExpandRect {
  top: number;
  left: number;
  width: number;
  height: number;
}

/** Fullscreen video player - only rendered when activated */
function FullscreenPlayer({
  videos,
  initialVideoIndex = 0,
  onClose,
  onVideoChange,
  initialExpandRect,
}: {
  videos: VideoItem[];
  initialVideoIndex: number;
  onClose?: () => void;
  onVideoChange?: (videoIndex: number) => void;
  initialExpandRect?: ExpandRect | null;
}) {
  const { optimizely, userFlags } = useOptimizely();
  const verticalVideoEnabled = (userFlags.vertical_video_scrolling as VariantProps | undefined)?.enabled ?? false;

  const trackVerticalVideoEvent = useCallback(
    async (eventKey: string, properties: Record<string, string | number> = {}, value = 1) => {
      const shouldTrack = verticalVideoEnabled && optimizely;
      if (!shouldTrack) {
        return;
      }
      try {
        const pageAdConfig =
          typeof window !== 'undefined' && 'arcSpanPageConfig' in window
            ? (window as Window & { arcSpanPageConfig?: PageAdConfig }).arcSpanPageConfig
            : undefined;
        const user = await getUserContext(optimizely, pageAdConfig);
        if (!user) {
          return;
        }
        const tags = { $opt_event_properties: properties, value };
        // biome-ignore lint/suspicious/noExplicitAny: Optimizely SDK user context
        (user as any).trackEvent(eventKey, tags);
      } catch {
        // ignore tracking errors
      }
    },
    [verticalVideoEnabled, optimizely],
  );

  const [currentIndex, setCurrentIndex] = useState(initialVideoIndex);
  const [isPlaying, setIsPlaying] = useState(true);
  const [isMuted, setIsMuted] = useState(true);
  const [progress, setProgress] = useState({ currentTime: 0, duration: 0 });
  const [controlsVisible, setControlsVisible] = useState(true);
  const [isVideoLoading, setIsVideoLoading] = useState(true);
  const [overscrollOffset, setOverscrollOffset] = useState(0);
  const initialTouchYRef = useRef<number | null>(null);
  const touchStartYRef = useRef<number | null>(null);
  const touchEndYRef = useRef<number | null>(null);
  const hasSentTotalTimeRef = useRef(false);
  const maxVideoIndexReachedRef = useRef(initialVideoIndex);
  const playerOpenTimeRef = useRef(Date.now());
  const videoStartTimeRef = useRef(Date.now());
  const hideControlsTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const isPlayingRef = useRef(isPlaying);
  const [isExpanded, setIsExpanded] = useState(!initialExpandRect);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const scrollAnimationRef = useRef<number | null>(null);
  const videoRefs = useRef<(HTMLVideoElement | null)[]>([]);

  const minSwipeDistance = 50;

  useEffect(() => {
    return () => {
      if (scrollAnimationRef.current !== null) {
        cancelAnimationFrame(scrollAnimationRef.current);
      }
    };
  }, []);

  const fireTimeSpentForCurrentVideo = useCallback(
    (_exitReason: 'swipe' | 'close' | 'ended' | 'scroll') => {
      if (currentIndex >= MAX_VIDEOS_FOR_TIME_SPENT) {
        return;
      }
      const currentVideoItem = videos[currentIndex];
      if (!currentVideoItem) {
        return;
      }
      const seconds = (Date.now() - videoStartTimeRef.current) / 1000;
      const eventKey = `vertical_vid_time_${currentIndex + 1}`;
      trackVerticalVideoEvent(eventKey, { video_id: currentVideoItem.id }, seconds);
    },
    [currentIndex, videos, trackVerticalVideoEvent],
  );

  useEffect(() => {
    trackVerticalVideoEvent('vertical_vid_player_open');
  }, [trackVerticalVideoEvent]);

  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState !== 'hidden') {
        return;
      }
      if (hasSentTotalTimeRef.current) {
        return;
      }
      hasSentTotalTimeRef.current = true;
      const totalPlayerSeconds = (Date.now() - playerOpenTimeRef.current) / 1000;
      trackVerticalVideoEvent('vertical_vid_player_total_time', {}, totalPlayerSeconds);
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [trackVerticalVideoEvent]);

  useEffect(() => {
    videoStartTimeRef.current = Date.now();
    if (currentIndex > maxVideoIndexReachedRef.current) {
      maxVideoIndexReachedRef.current = currentIndex;
    }
  }, [currentIndex]);

  useEffect(() => {
    if (initialExpandRect) {
      const frame = requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          setIsExpanded(true);
        });
      });
      return () => cancelAnimationFrame(frame);
    }
  }, [initialExpandRect]);

  useEffect(() => {
    if (initialVideoIndex <= 0) {
      return;
    }
    const scrollToInitial = () => {
      const container = scrollContainerRef.current;
      if (!container) {
        return;
      }
      const videoHeight = container.clientHeight;
      if (videoHeight > 0) {
        container.scrollTop = initialVideoIndex * videoHeight;
        setCurrentIndex(initialVideoIndex);
      }
    };
    // Defer to next frame so scrollContainerRef is populated after layout
    const id = requestAnimationFrame(() => {
      scrollToInitial();
      // Retry once in case container wasn't ready (e.g. during expand animation)
      requestAnimationFrame(scrollToInitial);
    });
    return () => cancelAnimationFrame(id);
  }, [initialVideoIndex]);

  isPlayingRef.current = isPlaying;

  const scheduleHideControls = useCallback(() => {
    if (hideControlsTimeoutRef.current) {
      clearTimeout(hideControlsTimeoutRef.current);
      hideControlsTimeoutRef.current = null;
    }
    hideControlsTimeoutRef.current = setTimeout(() => {
      if (!isPlayingRef.current) {
        return;
      }
      setControlsVisible(false);
      hideControlsTimeoutRef.current = null;
    }, CONTROLS_HIDE_DELAY_MS);
  }, []);

  const showControlsAndResetTimer = useCallback(() => {
    setControlsVisible(true);
    scheduleHideControls();
  }, [scheduleHideControls]);

  useEffect(() => {
    if (controlsVisible && isPlaying) {
      scheduleHideControls();
    } else if (!isPlaying && hideControlsTimeoutRef.current) {
      clearTimeout(hideControlsTimeoutRef.current);
      hideControlsTimeoutRef.current = null;
    }
    return () => {
      if (hideControlsTimeoutRef.current) {
        clearTimeout(hideControlsTimeoutRef.current);
      }
    };
  }, [controlsVisible, isPlaying, scheduleHideControls]);

  // Show controls when video changes - currentIndex intentionally in deps
  // biome-ignore lint/correctness/useExhaustiveDependencies: currentIndex triggers control visibility reset
  useEffect(() => {
    setControlsVisible(true);
    scheduleHideControls();
    setIsPlaying(true);
  }, [currentIndex, scheduleHideControls]);

  // Reset loading state when switching videos - show loading until new video can play
  useEffect(() => {
    const currentVideo = videoRefs.current[currentIndex];
    if (!currentVideo) {
      setIsVideoLoading(true);
      return;
    }
    setIsVideoLoading(currentVideo.readyState < VIDEO_READY_STATE_HAVE_FUTURE_DATA);
  }, [currentIndex]);

  useEffect(() => {
    const currentVideo = videoRefs.current[currentIndex];
    if (!currentVideo) {
      return;
    }

    videoRefs.current.forEach((video) => {
      if (video && video !== currentVideo) {
        video.pause();
      }
    });

    currentVideo.muted = isMuted;
    if (isPlaying) {
      currentVideo.play().catch(() => {
        // Autoplay may be blocked by browser policy
      });
    } else {
      currentVideo.pause();
    }
  }, [currentIndex, isPlaying, isMuted]);

  // Sync progress when switching videos
  useEffect(() => {
    const currentVideo = videoRefs.current[currentIndex];
    if (!currentVideo) {
      return;
    }
    setProgress({
      currentTime: currentVideo.currentTime,
      duration: currentVideo.duration || 0,
    });
  }, [currentIndex]);

  const scrollToVideo = useCallback((index: number) => {
    const container = scrollContainerRef.current;
    if (!container) {
      return;
    }
    const videoHeight = container.clientHeight;
    const targetScroll = index * videoHeight;

    if (scrollAnimationRef.current !== null) {
      cancelAnimationFrame(scrollAnimationRef.current);
      scrollAnimationRef.current = null;
    }

    const startScroll = container.scrollTop;
    const startTime = performance.now();

    const animate = (now: number) => {
      const elapsed = now - startTime;
      const t = Math.min(elapsed / SCROLL_ANIMATION_DURATION_MS, 1);
      const eased = easeOutCubic(t);
      container.scrollTop = startScroll + (targetScroll - startScroll) * eased;

      if (t < 1) {
        scrollAnimationRef.current = requestAnimationFrame(animate);
      } else {
        scrollAnimationRef.current = null;
      }
    };
    scrollAnimationRef.current = requestAnimationFrame(animate);
  }, []);

  const handleVideoEnd = useCallback(() => {
    fireTimeSpentForCurrentVideo('ended');
    const isLastVideo = currentIndex >= videos.length - 1;
    if (isLastVideo) {
      setIsPlaying(false);
      setControlsVisible(true);
    } else {
      const nextIndex = currentIndex + 1;
      setCurrentIndex(nextIndex);
      setControlsVisible(true);
      scrollToVideo(nextIndex);
      onVideoChange?.(nextIndex);
    }
  }, [currentIndex, fireTimeSpentForCurrentVideo, videos.length, scrollToVideo, onVideoChange]);

  const handleScroll = useCallback(
    (e: React.UIEvent<HTMLDivElement>) => {
      const scrollTop = e.currentTarget.scrollTop;
      const videoHeight = e.currentTarget.clientHeight;
      const newIndex = Math.round(scrollTop / videoHeight);
      if (newIndex !== currentIndex && newIndex >= 0 && newIndex < videos.length) {
        fireTimeSpentForCurrentVideo('scroll');
        setCurrentIndex(newIndex);
        onVideoChange?.(newIndex);
      }
    },
    [currentIndex, fireTimeSpentForCurrentVideo, onVideoChange, videos.length],
  );

  const handleTimeUpdate = useCallback(() => {
    const currentVideo = videoRefs.current[currentIndex];
    if (currentVideo) {
      setProgress({
        currentTime: currentVideo.currentTime,
        duration: currentVideo.duration || 0,
      });
    }
  }, [currentIndex]);

  const handleLoadedMetadata = useCallback(() => {
    const currentVideo = videoRefs.current[currentIndex];
    if (currentVideo) {
      setProgress({
        currentTime: currentVideo.currentTime,
        duration: currentVideo.duration || 0,
      });
    }
  }, [currentIndex]);

  const handleVideoTap = useCallback(() => {
    const currentVideoItem = videos[currentIndex];
    const eventKey = isPlaying ? 'vertical_vid_pause' : 'vertical_vid_play';
    trackVerticalVideoEvent(
      eventKey,
      currentVideoItem ? { video_id: currentVideoItem.id, video_index: currentIndex } : {},
    );
    setIsPlaying((p) => !p);
    if (controlsVisible) {
      scheduleHideControls();
    } else {
      showControlsAndResetTimer();
    }
  }, [
    controlsVisible,
    currentIndex,
    isPlaying,
    scheduleHideControls,
    showControlsAndResetTimer,
    trackVerticalVideoEvent,
    videos,
  ]);

  const handleMuteToggle = useCallback(() => {
    const currentVideoItem = videos[currentIndex];
    trackVerticalVideoEvent(
      isMuted ? 'vertical_vid_unmute' : 'vertical_vid_mute',
      currentVideoItem ? { video_id: currentVideoItem.id, video_index: currentIndex } : {},
    );
    setIsMuted((m) => !m);
    scheduleHideControls();
  }, [currentIndex, isMuted, scheduleHideControls, trackVerticalVideoEvent, videos]);

  const handleSeekComplete = useCallback(
    (newTime: number, duration: number) => {
      const currentVideoItem = videos[currentIndex];
      if (!currentVideoItem) {
        return;
      }
      const seekToPercent = duration > 0 ? (newTime / duration) * 100 : 0;
      trackVerticalVideoEvent('vertical_vid_seek', {
        seek_to_percent: Math.round(seekToPercent * 100) / 100,
        video_id: currentVideoItem.id,
        video_index: currentIndex,
      });
    },
    [currentIndex, trackVerticalVideoEvent, videos],
  );

  const handleSeek = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      const videoEl = videoRefs.current[currentIndex];
      if (!videoEl) {
        return;
      }
      if (!videoEl.duration) {
        return;
      }
      const rect = e.currentTarget.getBoundingClientRect();
      const x = e.clientX - rect.left;
      const percent = Math.max(0, Math.min(1, x / rect.width));
      const newTime = percent * videoEl.duration;
      videoEl.currentTime = newTime;
      setProgress({
        currentTime: newTime,
        duration: videoEl.duration,
      });
      handleSeekComplete(newTime, videoEl.duration);
    },
    [currentIndex, handleSeekComplete],
  );

  const handleSeekWithHideControls = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      handleSeek(e);
      scheduleHideControls();
    },
    [handleSeek, scheduleHideControls],
  );

  const maxOverscrollPx = 50;

  const onTouchStart = useCallback((e: React.TouchEvent) => {
    const y = e.targetTouches[0].clientY;
    touchStartYRef.current = y;
    touchEndYRef.current = null;
    initialTouchYRef.current = y;
    setOverscrollOffset(0);
  }, []);

  const onTouchMove = useCallback(
    (e: React.TouchEvent) => {
      const currentY = e.targetTouches[0].clientY;
      touchEndYRef.current = currentY;

      const startY = initialTouchYRef.current;
      if (startY === null) {
        return;
      }

      const deltaY = currentY - startY;

      if (currentIndex === 0 && deltaY > 0) {
        setOverscrollOffset(Math.min(deltaY * OVERSCROLL_DAMPING, maxOverscrollPx));
      } else if (currentIndex === videos.length - 1 && deltaY < 0) {
        setOverscrollOffset(Math.max(deltaY * OVERSCROLL_DAMPING, -maxOverscrollPx));
      } else {
        setOverscrollOffset(0);
      }
    },
    [currentIndex, videos.length],
  );

  // biome-ignore lint/complexity/noExcessiveCognitiveComplexity: Touch swipe logic requires multiple branches
  const onTouchEnd = useCallback(() => {
    setOverscrollOffset(0);

    const touchStartY = touchStartYRef.current;
    const touchEndY = touchEndYRef.current;
    if (touchStartY === null || touchEndY === null) {
      return;
    }

    const distance = touchStartY - touchEndY;
    const isUpSwipe = distance > minSwipeDistance;
    const isDownSwipe = distance < -minSwipeDistance;
    const canGoNext = currentIndex < videos.length - 1;
    const canGoPrev = currentIndex > 0;

    if (isUpSwipe && canGoNext) {
      const currentVideoItem = videos[currentIndex];
      trackVerticalVideoEvent(
        'vertical_vid_swipe_up',
        currentVideoItem ? { from_index: currentIndex, to_index: currentIndex + 1, video_id: currentVideoItem.id } : {},
      );
      fireTimeSpentForCurrentVideo('swipe');
      const newIndex = currentIndex + 1;
      setCurrentIndex(newIndex);
      onVideoChange?.(newIndex);
    } else if (isDownSwipe && canGoPrev) {
      const currentVideoItem = videos[currentIndex];
      trackVerticalVideoEvent(
        'vertical_vid_swipe_down',
        currentVideoItem ? { from_index: currentIndex, to_index: currentIndex - 1, video_id: currentVideoItem.id } : {},
      );
      fireTimeSpentForCurrentVideo('swipe');
      const newIndex = currentIndex - 1;
      setCurrentIndex(newIndex);
      onVideoChange?.(newIndex);
    } else if ((isUpSwipe && !canGoNext) || (isDownSwipe && !canGoPrev)) {
      scrollToVideo(currentIndex);
    }
  }, [currentIndex, fireTimeSpentForCurrentVideo, onVideoChange, scrollToVideo, trackVerticalVideoEvent, videos]);

  const handleExitFullscreen = useCallback(() => {
    const currentVideoItem = videos[currentIndex];
    fireTimeSpentForCurrentVideo('close');
    const totalPlayerSeconds = (Date.now() - playerOpenTimeRef.current) / 1000;
    const videosViewedCount = maxVideoIndexReachedRef.current + 1;
    if (!hasSentTotalTimeRef.current) {
      hasSentTotalTimeRef.current = true;
      trackVerticalVideoEvent('vertical_vid_player_total_time', {}, totalPlayerSeconds);
    }
    trackVerticalVideoEvent('vertical_vid_count', {}, videosViewedCount);
    trackVerticalVideoEvent(
      'vertical_vid_player_close',
      currentVideoItem ? { last_video_id: currentVideoItem.id, last_video_index: currentIndex } : {},
    );

    // Pause all videos and reset their state
    videoRefs.current.forEach((v) => {
      if (v) {
        v.pause();
        v.currentTime = 0;
      }
    });

    onClose?.();
  }, [currentIndex, fireTimeSpentForCurrentVideo, onClose, trackVerticalVideoEvent, videos]);

  const expandStyle =
    initialExpandRect && !isExpanded
      ? {
          borderRadius: 0,
          height: initialExpandRect.height,
          left: initialExpandRect.left,
          top: initialExpandRect.top,
          width: initialExpandRect.width,
        }
      : {
          borderRadius: 0,
          height: '100vh',
          left: 0,
          top: 0,
          width: '100vw',
        };

  return (
    <div
      className={cn(
        'vertical-video-player fixed flex flex-col overflow-hidden bg-black',
        'transition-[top,left,width,height,border-radius] duration-[400ms] ease-out',
        'z-[2147483647]',
      )}
      onTouchEnd={onTouchEnd}
      onTouchMove={onTouchMove}
      onTouchStart={onTouchStart}
      style={expandStyle}
    >
      <button
        aria-label='Close video player'
        className={cn(
          'absolute right-6 top-6 z-[10002] flex min-h-[44px] min-w-[44px] cursor-pointer items-center justify-center',
          'rounded-full border-none bg-transparent text-white opacity-90',
          'transition-opacity duration-300 ease-in-out',
        )}
        onClick={(e) => {
          e.stopPropagation();
          handleExitFullscreen();
        }}
        type='button'
      >
        <span className='flex h-10 w-10 items-center justify-center rounded-full bg-black/50'>
          <X
            size={24}
            strokeWidth={2}
          />
        </span>
      </button>

      {/* Loading overlay - centered, shown when current video is buffering */}
      {isVideoLoading && (
        <div className='absolute inset-0 z-[10001] flex items-center justify-center pointer-events-none'>
          <Loader2
            className='animate-spin text-white opacity-90'
            size={48}
            strokeWidth={2}
          />
        </div>
      )}

      <div
        className={cn(
          'relative z-[1] flex h-screen w-screen flex-col overflow-x-hidden overflow-y-auto',
          'scroll-smooth snap-y snap-mandatory',
          '[-webkit-overflow-scrolling:touch]',
        )}
        onScroll={handleScroll}
        ref={scrollContainerRef}
        style={{ overscrollBehavior: 'contain' }}
      >
        <div
          className='flex shrink-0 flex-col'
          style={{
            transform: `translateY(${overscrollOffset}px)`,
            transition: overscrollOffset === 0 ? 'transform 0.25s ease-out' : 'none',
          }}
        >
          {/* biome-ignore lint/complexity/noExcessiveCognitiveComplexity: Video slide has many conditional branches */}
          {videos.map((video, index) => (
            <div
              className='relative flex h-screen w-screen shrink-0 snap-start snap-always items-center justify-center'
              key={video.id}
            >
              <video
                className='h-full w-full object-cover'
                muted={index !== currentIndex ? true : isMuted}
                onCanPlay={() => {
                  if (index === currentIndex) {
                    setIsVideoLoading(false);
                    if (isPlaying) {
                      // biome-ignore lint/suspicious/noEmptyBlockStatements: Intentionally ignore play() rejection
                      videoRefs.current[index]?.play().catch(() => {});
                    }
                  }
                }}
                onClick={index === currentIndex ? handleVideoTap : undefined}
                onEnded={handleVideoEnd}
                onLoadedMetadata={index === currentIndex ? handleLoadedMetadata : undefined}
                onLoadStart={() => {
                  if (index === currentIndex) {
                    setIsVideoLoading(true);
                  }
                }}
                onTimeUpdate={index === currentIndex ? handleTimeUpdate : undefined}
                onWaiting={() => {
                  if (index === currentIndex) {
                    setIsVideoLoading(true);
                  }
                }}
                playsInline
                preload={
                  index < PRELOAD_INITIAL_COUNT || (index > currentIndex && index <= currentIndex + PRELOAD_NEXT_COUNT)
                    ? 'auto'
                    : 'metadata'
                }
                ref={(el) => {
                  videoRefs.current[index] = el;
                }}
                src={video.videoUrl}
              />
              {video.title && (
                <button
                  className={cn(
                    'absolute inset-x-0 top-0 z-[10001] flex min-h-10 w-full items-center border-none font-inherit text-left text-white',
                    'bg-[linear-gradient(to_bottom,rgba(0,0,0,0.9)_0%,rgba(0,0,0,0.55)_60%,transparent_100%)]',
                    'p-6 pr-[88px]',
                    'transition-opacity duration-300 ease-in-out',
                    index === currentIndex && controlsVisible
                      ? 'cursor-pointer opacity-100'
                      : 'cursor-default opacity-0 pointer-events-none',
                  )}
                  onClick={(e) => {
                    if (index !== currentIndex) {
                      return;
                    }
                    e.stopPropagation();
                    handleVideoTap();
                  }}
                  onKeyDown={(e) => {
                    if (index !== currentIndex) {
                      return;
                    }
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      handleVideoTap();
                    }
                  }}
                  type='button'
                >
                  <h3 className='m-0 text-lg font-bold'>{video.title}</h3>
                </button>
              )}
              {/* Swipe up hint - first video only */}
              {index === 0 && currentIndex === 0 && videos.length > 1 && (
                <div
                  className={cn(
                    'absolute bottom-6 left-1/2 z-[10002] flex -translate-x-1/2 flex-col items-center gap-2 text-white',
                    'transition-opacity duration-300 ease-in-out',
                    controlsVisible ? 'opacity-75' : 'opacity-0 pointer-events-none',
                  )}
                >
                  <span className='flex text-white animate-[icon-nudge-up_1.5s_ease-in-out_infinite]'>
                    <ChevronUp
                      className='text-white'
                      size={28}
                      strokeWidth={2}
                    />
                  </span>
                  <span className='text-sm font-medium leading-none text-white'>Swipe up for next video</span>
                </div>
              )}
              {/* Video controls - only for current video */}
              {index === currentIndex && (
                <div
                  className={cn(
                    'absolute inset-x-0 bottom-0 z-[10001]',
                    'transition-opacity duration-300 ease-in-out',
                    controlsVisible ? 'opacity-100' : 'opacity-0 pointer-events-none',
                  )}
                >
                  <VideoControls
                    currentIndex={currentIndex}
                    isMuted={isMuted}
                    isPlaying={isPlaying}
                    onMuteToggle={handleMuteToggle}
                    onPlayPause={handleVideoTap}
                    onSeek={handleSeekWithHideControls}
                    onSeekComplete={handleSeekComplete}
                    progress={progress}
                    videoRef={videoRefs}
                  />
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

const VerticalVideoPlayer = ({
  videos,
  initialVideoIndex = 0,
  onClose,
  onVideoChange,
  renderTrigger,
  scrollTriggerOnce = false,
}: VerticalVideoPlayerProps) => {
  const [isActive, setIsActive] = useState(false);
  const [initialExpandRect, setInitialExpandRect] = useState<{
    top: number;
    left: number;
    width: number;
    height: number;
  } | null>(null);
  const hasUserClosedRef = useRef(false);
  const thumbnailRef = useRef<HTMLDivElement | null>(null);
  const { ref: inViewRef, inView } = useInView({
    threshold: 0.5,
    triggerOnce: scrollTriggerOnce ?? false,
  });

  const setThumbnailRef = useCallback(
    (el: HTMLDivElement | null) => {
      thumbnailRef.current = el;
      if (typeof inViewRef === 'function') {
        inViewRef(el);
      } else if (inViewRef) {
        (inViewRef as React.MutableRefObject<HTMLDivElement | null>).current = el;
      }
    },
    [inViewRef],
  );

  const activateWithRect = useCallback(() => {
    const rect = thumbnailRef.current?.getBoundingClientRect();
    if (rect) {
      setInitialExpandRect({
        height: rect.height,
        left: rect.left,
        top: rect.top,
        width: rect.width,
      });
    } else {
      setInitialExpandRect(null);
    }
    setIsActive(true);
  }, []);

  // Activate fullscreen when scrolled 50% into viewport (only if user hasn't closed it before)
  useEffect(() => {
    if (inView && !isActive && !hasUserClosedRef.current) {
      activateWithRect();
    }
  }, [inView, isActive, activateWithRect]);

  const handleActivate = useCallback(() => {
    activateWithRect();
  }, [activateWithRect]);

  const handleClose = useCallback(() => {
    hasUserClosedRef.current = true;
    setIsActive(false);
    setInitialExpandRect(null);
    onClose?.();
  }, [onClose]);

  // Dispatch event when video player opens/closes (for Google One Tap, STN, etc.)
  useEffect(() => {
    window.dispatchEvent(new CustomEvent('verticalVideoPlayerStateChange', { detail: { isOpen: isActive } }));
  }, [isActive]);

  // Hide OneTrust floating button while video player is open.
  const onetrustHideStyleId = 'vertical-video-onetrust-hide';
  useEffect(() => {
    const applyHide = (el: HTMLElement) => {
      el.style.setProperty('display', 'none', 'important');
      el.style.setProperty('visibility', 'hidden', 'important');
      el.style.setProperty('pointer-events', 'none', 'important');
    };
    const removeHide = (el: HTMLElement) => {
      el.style.removeProperty('display');
      el.style.removeProperty('visibility');
      el.style.removeProperty('pointer-events');
    };

    const hideOrShow = (otEl: HTMLElement) => {
      if (isActive) {
        applyHide(otEl);
      } else {
        removeHide(otEl);
      }
    };

    // Try common OneTrust floating button selectors
    const selectors = ['#ot-sdk-btn-floating', '[id^="ot-sdk-btn"]', '.ot-floating-button'];
    const findButton = () => {
      for (const sel of selectors) {
        try {
          const el = document.querySelector(sel);
          if (el instanceof HTMLElement) {
            return el;
          }
        } catch {
          // invalid selector, skip
        }
      }
      return null;
    };

    // Apply immediately if button exists
    const otButton = findButton();
    if (otButton) {
      hideOrShow(otButton);
    }

    // Inject global style as fallback (applies when element appears)
    let styleEl = document.getElementById(onetrustHideStyleId) as HTMLStyleElement | null;
    if (isActive) {
      if (!styleEl) {
        styleEl = document.createElement('style');
        styleEl.id = onetrustHideStyleId;
        styleEl.textContent = `
          #ot-sdk-btn-floating,
          [id^="ot-sdk-btn"],
          .ot-floating-button {
            display: none !important;
            visibility: hidden !important;
            pointer-events: none !important;
          }
        `;
        document.head.appendChild(styleEl);
      }
    } else {
      styleEl?.remove();
    }

    return () => {
      document.getElementById(onetrustHideStyleId)?.remove();
      const otEl = findButton();
      if (otEl) {
        removeHide(otEl);
      }
    };
  }, [isActive]);

  if (!videos || videos.length === 0) {
    return null;
  }

  const triggerContent = renderTrigger({ onActivate: handleActivate, setTriggerRef: setThumbnailRef });

  return (
    <>
      {/* Trigger: custom element provided by renderTrigger */}
      {triggerContent}

      {/* Fullscreen player - rendered in portal to body for stacking above ads */}
      {isActive &&
        typeof document !== 'undefined' &&
        createPortal(
          <FullscreenPlayer
            initialExpandRect={initialExpandRect}
            initialVideoIndex={initialVideoIndex}
            onClose={handleClose}
            onVideoChange={onVideoChange}
            videos={videos}
          />,
          document.body,
        )}
    </>
  );
};

export { VerticalVideoPlayer };
